import React, { FC, useEffect, useMemo, useRef } from "react"
import { Box, Title, ChatBox, ChatProps } from "dash-styled"
import {
    APIJobModel,
    JobStatus
} from "../../../../../../../Models/API/JobModel"
import { useAppState } from "../../../../../../../Hooks/useReduxState"
import { getFeedbackTagColor } from "../../Common/constants"
import { SendMessage } from "../../Common/SendMessage"
import {
    APIFeedbackMessage,
    APIFeedbackTag,
    GroupedMessages,
    groupMessagesByCreatorId,
    filterFeedbackByChildTags,
    getWorkerId,
    FeedbackMessageViewState,
    FeedbackMessageCreatorType
} from "../../../../../../../Models/API/JobFeedbackModel"
import { useDispatch } from "react-redux"
import { updateFeedbackMessageViewState } from "../../../../../../../Actions/API/Job/JobActions"
import { selectUserId } from "../../../../../../../Helpers/Functions/Selectors/auth"
import { UITaskModel } from "../../../../../../../Models/API/TaskModel"

type GroupedChats = { [messageCreatorId: string]: ChatProps[] }

interface FeedbackMessagesProps {
    appID: string
    job: APIJobModel
    messages?: APIFeedbackMessage[]
    onMessageSend: (message: string) => Promise<any>
    feedbackTags?: APIFeedbackTag[]
    taskList: UITaskModel[]
}

export const FeedbackMessages: FC<FeedbackMessagesProps> = ({
    appID,
    job,
    messages,
    onMessageSend,
    feedbackTags,
    taskList
}) => {
    const ownerID = useAppState(selectUserId)
    const scrollRef = useRef<HTMLDivElement>(null)

    const dispatch = useDispatch()
    useEffect(() => {
        if (
            !!messages &&
            messages.findIndex(
                m => m.viewState === FeedbackMessageViewState.UNREAD
            ) !== -1
        ) {
            dispatch(updateFeedbackMessageViewState(job.id, appID))
        }
    }, [appID, dispatch, job.id, messages])

    const groupedMessages: GroupedChats = useMemo(() => {
        if (!messages) {
            return {}
        }

        const groupedMessagesByCreatorId: GroupedMessages =
            groupMessagesByCreatorId(messages, taskList)
        const groupedChatsByCreatorId: GroupedChats = {}

        for (const key in groupedMessagesByCreatorId) {
            groupedChatsByCreatorId[key] = filterFeedbackByChildTags(
                groupedMessagesByCreatorId[key]
            ).map(message => ({
                tag: message?.tag?.tag
                    ? {
                          value: message?.tag?.tag ?? "",
                          chipColor: getFeedbackTagColor(
                              message.tag,
                              feedbackTags
                          )
                      }
                    : undefined,
                message: {
                    value: message.message,
                    className: "break-words hyphens-auto whitespace-pre-wrap"
                }
            }))
        }
        return groupedChatsByCreatorId
    }, [feedbackTags, messages, taskList])

    useEffect(() => {
        if (messages && messages?.length > 0) {
            scrollRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
                inline: "start"
            })
        }
    }, [messages])

    const renderMessage = ([messageCreatorKey, chats]: [
        string,
        ChatProps[]
    ]) => {
        const [creatorId, creatorType, creatorLabel, , createdDate, taskName] =
            messageCreatorKey.split("/")
        const isOwner = ownerID === creatorId.toString()
        return (
            <React.Fragment key={messageCreatorKey}>
                <ChatBox
                    chats={chats}
                    className={`${chats.every(c => !c.tag) ? "py-2" : ""} ${
                        creatorType === FeedbackMessageCreatorType.OWNER
                            ? "bg-transparent border border-smoke-950 border-solid"
                            : ""
                    }`}
                />
                <div
                    className={`mt-1 mb-3 w-full text-xs text-rhino-900 hyphens-auto ${
                        creatorType === FeedbackMessageCreatorType.OWNER
                            ? "text-right"
                            : "text-left"
                    }`}
                >
                    {taskName ? (
                        <span className="font-semibold">{`(${taskName}) `}</span>
                    ) : null}
                    {isOwner
                        ? "You, "
                        : `${creatorLabel}${
                              creatorType === FeedbackMessageCreatorType.WORKER
                                  ? " " + getWorkerId(creatorId)
                                  : ""
                          }, `}

                    {createdDate}
                </div>
            </React.Fragment>
        )
    }

    if (
        (messages && messages.length > 0) ||
        job.status === JobStatus.COMPLETED
    ) {
        return (
            <Box fullWidth={true} testId="feedback-message-view">
                <Title variant="lg" className="text-rhino-900">
                    Messages
                </Title>
                {Object.entries(groupedMessages).map(renderMessage)}
                <SendMessage onSend={onMessageSend} />
                <div ref={scrollRef} />
            </Box>
        )
    }
    return null
}
